import React, {useEffect, useState} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {useQuery,useMutation, useApolloClient} from '@apollo/client';
import {UserQuery,UserQueryData,UserQueryVars, UserGamesQuery, UserGamesQueryData, UserMapQuery,UserMapQueryData,
UserPlayedWithQuery, UserPlayedWithQueryData} from '@pvplevelup/core/dist/queries/user/user';
import { RouteComponentProps } from 'react-router-dom';
import { Tabs, Tab, Typography, Box, useMediaQuery  } from '@material-ui/core';
import {BarChart, Map, Games, Group, Person, SportsKabaddi, Explore, EmojiEvents} from '@material-ui/icons'
import { UserPlayedWith } from 'components/User/UserPlayedWith';
import { UserStats, breakPoint } from 'components/User/UserStats';
import { UserMaps } from 'components/User/UserMaps';
import { UserGames } from 'components/User/UserGames';
import { UserHeader } from 'components/User/UserHeader';
import moment from 'moment';
import { DuelsTab } from 'views/Duel/DuelsTab';
import { PveRunList } from 'views/Pve/run/PveRunList';
import { TournamentHistory } from 'views/Tournament/TournamentHistory';
import { UserFilters } from 'components/User/UserFilters';
import _ from 'lodash'
import { ProfileTabPanel as TabPanel, MobileFilters, useProfileLayoutStyles } from 'components/Profile/ProfileLayout';
import { useIsMobile } from 'components/Page/useIsMobile';
import { useMountedState } from 'components/useMountedState';
import { LeaderboardSeasonsData, LeaderboardSeasonsQuery } from '@pvplevelup/core/dist/queries/leaderboard/seasons';
import { ISeason } from '@pvplevelup/core/dist/interfaces/ISeason';
import { UserCharacters } from 'components/User/UserCharacters';

const useStyles = makeStyles(theme => ({
  // the app bar is 56px on phones (48px in landscape) and 64px from the sm breakpoint up
  page: {
    height: 'calc(100vh - 56px)',
    '@media (orientation: landscape)': {
      height: 'calc(100vh - 48px)',
    },
    [theme.breakpoints.up('sm')]: {
      height: 'calc(100vh - 64px)',
    },
  },
  nav: {
    padding: theme.spacing(1.25, 0, 1.25, 1.25),
  },
  tabsRoot: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: theme.spacing(0.5, 0),
  },
  indicator: {
    width: 3,
    borderRadius: 2,
  },
  tab: {
    minWidth: 'auto',
    minHeight: 44,
    padding: theme.spacing(0.5, 2),
    textTransform: 'none',
    fontSize: '0.92rem',
    opacity: 0.75,
    '&.Mui-selected': {
      opacity: 1,
      background: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      opacity: 1,
    },
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    '& .MuiTab-wrapper > *:first-child': {
      marginBottom: 0,
      marginRight: theme.spacing(1.25),
      fontSize: '1.25rem',
    },
  },
  content: {
    padding: theme.spacing(1.25, 1.5),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    // the stat cards and tables inside each tab
    '& .MuiPaper-root': {
      background: 'rgba(20, 20, 20, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      boxShadow: 'none',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
  },
  filtersPanel: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 1.5, 0.5),
    marginBottom: theme.spacing(2),
  },
  filtersTitle: {
    fontSize: '0.75em',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.65,
    margin: theme.spacing(0, 0, 1, 1),
  },
}));

interface Props{
}

interface RouteParams {
  id?: string;
}

export enum FetchType {
  Games,
  PlayedWith
}

const User: React.FC<Props & RouteComponentProps<RouteParams>> = (props) => {
  const classes = useStyles();
  const apolloClient = useApolloClient();
  const [season, setSeason] = useState<number>(1);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [ranked, setRanked] = useState("Ranked");
  const [zones, setZones] = useState<number[]>([]);
  const [maps, setMaps] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [sizes, setSizes] = useState<number[]>([]);
  const [modes, setModes] = useState<string[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filtersDefault, setFiltersDefault] = useState(true);
  const [byCharacter, setByUser] = useState<boolean>(true);
  const isMounted = useMountedState();


  const { data:{user = null,userStats,classStats,fieldStats} = {},loading, error, refetch,client } = useQuery<UserQueryData,UserQueryVars>(
    UserQuery,
    { variables: {user: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network'},
  );

  const { data:{userMapStats} = {}, loading: mapLoading, fetchMore: mapFetchMore, refetch: mapRefetch } = useQuery<UserMapQueryData,UserQueryVars>(
    UserMapQuery,
    { variables: {user: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network' },
  );

  const { data:{userPlayedWithStats} = {}, loading: playedWithLoading, fetchMore: playedWithFetchMore, refetch: playedWithRefetch } = useQuery<UserPlayedWithQueryData,UserQueryVars>(
    UserPlayedWithQuery,
    { variables: {user: props.match.params.id || "", ranked: ranked,season:season,byCharacter:byCharacter}, fetchPolicy: 'cache-and-network' },
  );

  const { data:{gamesByUser} = {}, loading: gamesLoading, fetchMore: gamesFetchMore, refetch: gamesRefetch } = useQuery<UserGamesQueryData,UserQueryVars>(
    UserGamesQuery,
    { variables: {user: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network' },
  );

  useEffect(()=>{
    if(isMounted()){
      apolloClient.query<LeaderboardSeasonsData>({query:LeaderboardSeasonsQuery}).then((res)=>{
        if(res.data){
          if(res.data.currentSeason){
            setSeason(res.data.currentSeason.season);
          }
          setSeasons(res.data.seasons || []);
        }
      })
    }
  },[])

  const onChangeRanked = (value: string) => {
    setFiltersChanged(true);
    setRanked(value);
  }

  const onChangeZones = (value: number[]) => {
    setFiltersChanged(true);
    setZones(value);
  }

  const onChangeMaps = (value: number[]) => {
    setFiltersChanged(true);
    setMaps(value);
  }

  const onChangeStartDate = (value: string) => {
    setFiltersChanged(true);
    setStartDate(value);
  }

  const onChangeEndDate = (value: string) => {
    setFiltersChanged(true);
    setEndDate(value);
  }

  const onChangeSizes = (value: number[]) => {
    setFiltersChanged(true);
    setSizes(value);
  }

  const onChangeModes = (value: string[]) => {
    setFiltersChanged(true);
    setModes(value);
  }

  const onChangeSeason = (value: number) => {
    setFiltersChanged(true);
    setSeason(value);
  }

  const onChangeByUser = (value: boolean) => {
    setByUser(value);
    let variables:UserQueryVars = {user: props.match.params.id || "",ranked:ranked,season:season};
    if(zones) variables.zones = zones;
    if(maps) variables.maps = maps;
    variables.startDate = startDate;
    variables.endDate = endDate;
    if(sizes) variables.sizes = sizes;
    if(modes) variables.modes = modes;
    playedWithRefetch({...variables,byCharacter:value});
  }

  const onRefetch = (ranked:string,zones:number[],maps:number[],startDate:string | null,endDate:string | null,sizes:number[],modes:string[],filtersChanged:boolean) => {
    if(filtersChanged){
      let variables:UserQueryVars = {user: props.match.params.id || "",ranked:ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      let duelVariables: any = {...variables};
      delete duelVariables.sizes;
      delete duelVariables.modes;
      delete duelVariables.maps;

      setFiltersChanged(false);
      setFiltersDefault(false);
      refetch(variables);
      mapRefetch(variables);
      gamesRefetch(variables);
      playedWithRefetch({...variables,byCharacter:byCharacter});
    }
  }

  const onResetFilters = () => {
    setRanked("Ranked");
    setZones([]);
    setMaps([]);
    setStartDate(null);
    setEndDate(null);
    setSizes([]);
    setModes([]);
    if(!filtersDefault){
      onRefetch("Ranked",[],[],null,null,[],[],true);
      setFiltersDefault(true);
    }
  }

  //const {data:{user = null,userMapStats,userPlayedWithStats,gamesByUser,userStats,classStats} = {},loading, error, fetchMore} = result;
  //console.log(userMapStats,userPlayedWithStats,gamesByUser,userStats);

  const fetchMoreMapsRecords = ()=>{
    if(!mapLoading && userMapStats?.hasNext){
      let variables:UserQueryVars = {user: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = userMapStats.next;
      mapFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;

          let userMapStats = [...prev.userMapStats?.maps || [], ...fetchMoreResult.userMapStats?.maps || []];
          userMapStats = _.reverse(_.sortBy(userMapStats,(map)=>map.played));
          userMapStats = _.uniqBy(userMapStats,(map)=>map.map);

          return Object.assign({}, prev, {
            userMapStats:{
              next: fetchMoreResult.userMapStats?.next,
              previous: fetchMoreResult.userMapStats?.previous,
              hasNext: fetchMoreResult.userMapStats?.hasNext,
              hasPrevious: fetchMoreResult.userMapStats?.next,
              maps: userMapStats,
              __typename: fetchMoreResult.userMapStats?.__typename
            }
          });
        }
      })
    }
  }

  const fetchMoreGameRecords = ()=>{
    if(!gamesLoading && gamesByUser?.hasNext){
      let variables:UserQueryVars = {user: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = gamesByUser.next;
      gamesFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;

          let gamesByUser = [...prev.gamesByUser?.games || [], ...fetchMoreResult.gamesByUser?.games || []];
          gamesByUser = _.reverse(_.sortBy(gamesByUser,(game)=>game.createdAt));
          gamesByUser = _.uniqBy(gamesByUser,(game)=>game._id);

          return Object.assign({}, prev, {
            gamesByUser:{
              next: fetchMoreResult.gamesByUser?.next,
              previous: fetchMoreResult.gamesByUser?.previous,
              hasNext: fetchMoreResult.gamesByUser?.hasNext,
              hasPrevious: fetchMoreResult.gamesByUser?.next,
              games: gamesByUser,
              __typename: fetchMoreResult.gamesByUser?.__typename
            }
          });
        }
      })
    }
  }

  const fetchMorePlayedWithRecords = ()=>{
    if(!playedWithLoading && userPlayedWithStats?.hasNext){
      let variables:UserQueryVars = {user: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      variables.byCharacter = byCharacter;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = userPlayedWithStats.next;
      playedWithFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;
          let userPlayedWithStats = [...prev.userPlayedWithStats?.stats || [], ...fetchMoreResult.userPlayedWithStats?.stats || []];
          //userPlayedWithStats = _.reverse(_.sortBy(userPlayedWithStats,(map)=>map.played));
          userPlayedWithStats = _.uniqBy(userPlayedWithStats,(map)=>byCharacter ? map.playedWith?._id : map.playedWithUser?._id);

          return Object.assign({}, prev, {
            userPlayedWithStats:{
              next: fetchMoreResult.userPlayedWithStats?.next,
              previous: fetchMoreResult.userPlayedWithStats?.previous,
              hasNext: fetchMoreResult.userPlayedWithStats?.hasNext,
              hasPrevious: fetchMoreResult.userPlayedWithStats?.next,
              stats: userPlayedWithStats,
              __typename: fetchMoreResult.userPlayedWithStats?.__typename
            }
          });
        }
      })
    }
  }

  //console.log(seasons);
  
  const [activeTab, setActiveTab] = useState(0);
  const DUELS_TAB = 5;
  const DUNGEONS_TAB = 6;
  const TOURNAMENTS_TAB = 7;
  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);

  const mobile = useIsMobile();
  const layout = useProfileLayoutStyles();
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters = (ranked != "Ranked" ? 1 : 0) + (zones.length ? 1 : 0) + (maps.length ? 1 : 0) + (startDate || endDate ? 1 : 0) + (sizes.length ? 1 : 0) + (modes.length ? 1 : 0);
  const hasFilters = activeTab != DUELS_TAB && activeTab != DUNGEONS_TAB;

  const profile = (
    <UserHeader userStats={userStats} fieldStats={fieldStats} user={user} lastGame={(gamesByUser && gamesByUser.games && gamesByUser.games[0]) ? moment(gamesByUser.games[0].createdAt).format("ddd, MMM Do YYYY, h:mm a") : ""} />
  );
  const filters = (
    <UserFilters filtersChanged={filtersChanged} ranked={ranked} zones={zones} maps={maps} startDate={startDate} endDate={endDate} sizes={sizes} modes={modes}
      resetFilters={onResetFilters} applyFilters={onRefetch} onChangeRanked={onChangeRanked} onChangeZones={onChangeZones}
      onChangeMaps={onChangeMaps} onChangeStartDate={onChangeStartDate} onChangeEndDate={onChangeEndDate} onChangeSizes={onChangeSizes} onChangeModes={onChangeModes}
      onChangeSeason={onChangeSeason} seasons={seasons} season={season}
    />
  );

  return (
    <div className={mobile ? layout.mobilePage : classes.page}>
      {(!error) &&
        <div className="character-container"
          style={mobile ? {display: 'block', height: 'auto', overflow: 'visible'} : {
            gridTemplateColumns: matches ? '1fr' : '11rem 1fr',
            gridTemplateRows: matches ? 'max-content minmax(0, 1fr)' : 'minmax(0, 1fr)',
            height: '100%',
            gridTemplateAreas: matches ? `'character-tabs' 'character-content'` : `'character-tabs character-content'`,
          }}
        >
          {mobile && profile}
          <div className={mobile ? layout.mobileTabs : `character-tabs ${classes.nav}`}>
            <Tabs
              orientation={matches ? "horizontal" :"vertical"}
              variant="scrollable"
              scrollButtons={matches && !mobile ? "auto" : "off"}
              value={activeTab}
              onChange={(e,value)=>setActiveTab(value)}
              aria-label=""
              classes={{root: classes.tabsRoot, indicator: classes.indicator}}

            >
              <Tab className={classes.tab} icon={<Person />} label="Characters" />
              <Tab className={classes.tab} icon={<BarChart />} label="Stats" />
              <Tab className={classes.tab} icon={<Map />} label="Maps" />
              <Tab className={classes.tab} icon={<Games />} label="Game History" />
              <Tab className={classes.tab} icon={<Group />} label="Played With" />
              <Tab className={classes.tab} icon={<SportsKabaddi />} label="Duels" />
              <Tab className={classes.tab} icon={<Explore />} label="Dungeons" />
              <Tab className={classes.tab} icon={<EmojiEvents />} label="Tournaments" />
            </Tabs>
          </div>
          <div className={mobile ? layout.mobileContent : `character-content ${classes.content}`}>
            {mobile ?
              hasFilters &&
                <MobileFilters active={activeFilters} open={showFilters} onToggle={() => setShowFilters(!showFilters)}>{filters}</MobileFilters>
            :
              <div className="character-header" style={{flexShrink: 0}}>
                {profile}
                {hasFilters &&
                  <div className={classes.filtersPanel}>
                    <div className={classes.filtersTitle}>Filters</div>
                    {filters}
                  </div>
                }
              </div>
            }
            <TabPanel value={activeTab} index={0} mobile={mobile}>
              <UserCharacters user={user} stats={userStats} classStats={classStats} />
            </TabPanel>
            <TabPanel value={activeTab} index={1} mobile={mobile}>
              <UserStats user={user} stats={userStats} classStats={classStats} mapStats={userMapStats} gamesByUser={gamesByUser} />
            </TabPanel>
            <TabPanel value={activeTab} index={2} mobile={mobile} fill>
              <UserMaps mapFeed={userMapStats} fetchMoreRecords={fetchMoreMapsRecords} />
            </TabPanel>
            <TabPanel value={activeTab} index={3} mobile={mobile} fill>
              <UserGames user={user} gameFeed={gamesByUser} fetchMoreRecords={fetchMoreGameRecords} />
            </TabPanel>
            <TabPanel value={activeTab} index={4} mobile={mobile} fill>
              <UserPlayedWith playedWithFeed={userPlayedWithStats} fetchMoreRecords={fetchMorePlayedWithRecords} byCharacter={byCharacter} onChangeByUser={onChangeByUser} />
            </TabPanel>
            <TabPanel value={activeTab} index={DUELS_TAB} mobile={mobile}>
              <DuelsTab userId={props.match.params.id} />
            </TabPanel>
            <TabPanel value={activeTab} index={DUNGEONS_TAB} mobile={mobile}>
              <PveRunList userId={props.match.params.id} />
            </TabPanel>
            <TabPanel value={activeTab} index={TOURNAMENTS_TAB} mobile={mobile}>
              <TournamentHistory userId={props.match.params.id} />
            </TabPanel>
          </div>
        </div>
      }
    </div>
  )
}

export {User};